<template>
  <div id="candidate-civil-state-gender">
    <vx-card title="Candidatos por estado civil y genero">
      <!-- LOADING -->
      <div
        v-if="uiState === 'loading'"
        class="gw-empty-state"
        style="height:350px;"
      >
        <div class="gw-spinner" aria-label="Cargando"></div>
        <div class="gw-empty-title">Cargando información…</div>
        <div class="gw-empty-subtitle">Un momento por favor</div>
      </div>

      <!-- READY (CHART) -->
      <VueApexCharts
        v-else-if="uiState === 'ready' && renderGraphic"
        type="bar"
        height="350"
        :options="chartOptions"
        :series="series"
      />

      <!-- EMPTY / ERROR -->
      <div
        v-else
        class="gw-empty-state"
        style="height:350px;"
      >
        <img
          class="gw-empty-img"
          :src="emptyStateImage"
          :alt="emptyStateAlt"
        />
        <div class="gw-empty-title">{{ emptyStateTitle }}</div>
        <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import VueApexCharts from "vue-apexcharts";
import api from "../../mixins/api";
import { mapGetters } from "vuex";
import { eventBus } from "./../../main";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      renderGraphic: false,
      requestKey: null,
      consultHandler: null,

      IMG_EMPTY:
        publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR:
        publicAssetUrl('Assets/illustrations/error.png'),

      series: [
        { name: "", data: [] },
        { name: "", data: [] },
      ],

      chartOptions: {
        chart: {
          type: "bar",
          height: 350,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "70%",
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        colors: ["#5F0091", "#FFCF44"],
        xaxis: {
          categories: ["Soltero", "Casado", "Viudo", "unión libre"],
        },
        yaxis: {
          title: {
            text: "Miles",
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " candidatos";
            },
          },
        },
      },
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },
    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },
    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },
    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      // ⚠️ tu código original tenía 'metrics/...' sin slash inicial; lo mantengo tal cual.
      let path = `metrics/candidate-civil-state-gender?client_id=${this.userInfo.user.client_id}`;
      let url = "";

      if (this.startDate != null && this.endDate != null) {
        let params = `&start_date=${this.startDate}&end_date=${this.endDate}`;
        url = `${this.url()}${path}${params}`;
      } else {
        url = `${this.url()}${path}`;
      }

      if (this.cityId != null) {
        url += `&city_id=${this.cityId}`;
      }

      return url;
    },

    resetChart() {
      this.renderGraphic = false;
      this.series = [
        { name: "", data: [] },
        { name: "", data: [] },
      ];
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetChart();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetChart();
    },

    setError() {
      this.uiState = "error";
      this.resetChart();
    },

    hasAnyValue(seriesArr) {
      if (!Array.isArray(seriesArr)) return false;

      for (let i = 0; i < seriesArr.length; i++) {
        const row = seriesArr[i];
        if (!Array.isArray(row)) continue;

        for (let j = 0; j < row.length; j++) {
          let v = row[j];
          if (typeof v === "string") v = parseFloat(v);
          if (typeof v === "number" && !isNaN(v) && v > 0) return true;
        }
      }
      return false;
    },

    async setReady(ranges, seriesArr) {
      // Force remount (evita glitches Apex)
      this.renderGraphic = false;
      await this.$nextTick();

      this.series = [
        { name: ranges[0] || "", data: seriesArr[0] || [] },
        { name: ranges[1] || "", data: seriesArr[1] || [] },
      ];

      this.renderGraphic = true;
      this.uiState = "ready";
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      const key = `${this.startDate || ""}|${this.endDate || ""}|${this.cityId || ""}`;
      this.setLoading(key);

      console.log("[candidate-civil-state-gender] initializeData:", {
        origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        const request = await this.requestGetWithoutContent(this.getUrl(), {});

        // Race condition guard
        if (this.requestKey !== key) {
          console.log("[candidate-civil-state-gender] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn(
            "[candidate-civil-state-gender] request not ok:",
            request && request.status
          );
          this.setError();
          return;
        }

        const data = request.data || {};
        const ranges = data.ranges; // tu backend lo envía como ranges
        const seriesArr = data.series;

        // Validación estructura mínima
        if (!Array.isArray(ranges) || ranges.length < 2) {
          console.warn("[candidate-civil-state-gender] ranges invalid:", ranges);
          this.setEmpty();
          return;
        }

        if (!Array.isArray(seriesArr) || seriesArr.length < 2) {
          console.warn("[candidate-civil-state-gender] series invalid:", seriesArr);
          this.setEmpty();
          return;
        }

        // Si todo está vacío/0 => empty
        if (!this.hasAnyValue(seriesArr)) {
          console.warn("[candidate-civil-state-gender] series without positive values");
          this.setEmpty();
          return;
        }

        await this.setReady(ranges, seriesArr);
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[candidate-civil-state-gender] request exception:", e);
        this.setError();
      }
    },
  },

  mounted() {
    this.initializeData("mounted");

    this.consultHandler = () =>
      this.initializeData("eventBus:consultData");
    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) {
      eventBus.$off("consultData", this.consultHandler);
    }
  },

  components: {
    VueApexCharts,
  },
};
</script>

<style scoped>
.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
